import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, Snackbar, Switch, FormControlLabel,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';

const _BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/espacios';
const API = _BASE.replace(/\/api\/espacios.*$/, '') + '/api';

function formatFilters(filters) {
  if (!filters) return 'Sin filtros';
  const parts = Object.entries(filters)
    .filter(([, v]) => v && (Array.isArray(v) ? v.length > 0 : v !== ''))
    .map(([k, v]) => `${k.replace('_', ' ')}: ${Array.isArray(v) ? v.join(', ') : v}`);
  return parts.length ? parts.join(' · ') : 'Sin filtros';
}

function formatExpiration(expiresAt) {
  if (!expiresAt) return '';
  const expDate = new Date(expiresAt);
  if (isNaN(expDate.getTime())) return ''; // Invalid date
  
  const diffMs = expDate - new Date();
  
  if (diffMs <= 0) return ` • EXPIRO el ${expDate.toLocaleString('es-DO')}`;
  
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const diffMins = Math.floor((diffMs / 1000 / 60) % 60);
  
  let timeLeft = '';
  if (diffDays > 0) timeLeft = `falta ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  else if (diffHours > 0) timeLeft = `falta ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  else timeLeft = `falta ${diffMins} min`;

  return ` • Vence el ${expDate.toLocaleString('es-DO')} (${timeLeft})`;
}

export default function ShareLinksManager({ 
  links, 
  loading, 
  onRefresh,
  hasMore,
  loadingMore,
  onLoadMore,
  filterCreatedBy,
  setFilterCreatedBy,
  users = [],
  isAdmin = false
}) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editDialog, setEditDialog] = useState({ open: false, link: null, name: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, link: null });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const getUserEmail = (uid) => {
    if (!uid) return 'N/A';
    const user = users.find(u => u.uid === uid);
    return user ? user.email : uid;
  };

  const copyLink = (shareId) => {
    const url = `${window.location.origin}/view/${shareId}`;
    navigator.clipboard.writeText(url);
    notify('Enlace copiado al portapapeles');
  };

  const openLink = (shareId) => {
    window.open(`${window.location.origin}/view/${shareId}`, '_blank');
  };

  const toggleActive = useCallback(async (link) => {
    try {
      await axios.patch(`${API}/share/${link.share_id}`, { active: !link.active });
      notify(link.active ? 'Enlace desactivado' : 'Enlace activado');
      onRefresh();
    } catch {
      notify('Error al cambiar estado del enlace', 'error');
    }
  }, [onRefresh]);

  const saveEdit = useCallback(async () => {
    let newExpiresAt = null;
    if (editDialog.expires_at) {
      const expDate = new Date(editDialog.expires_at);
      if (expDate <= new Date()) {
        notify('La fecha de vencimiento no puede ser en el pasado', 'error');
        return;
      }
      newExpiresAt = expDate.toISOString();
    }

    const wasExpired = editDialog.link.expires_at && new Date(editDialog.link.expires_at) <= new Date();
    // It's "now valid" if we cleared it (infinite) or set a future date
    const isNowValid = !newExpiresAt || new Date(newExpiresAt) > new Date();
    const shouldReactivate = wasExpired && isNowValid;

    try {
      await axios.patch(`${API}/share/${editDialog.link.share_id}`, { 
        name: editDialog.name,
        expires_at: newExpiresAt,
        active: shouldReactivate ? true : undefined
      });
      notify(shouldReactivate ? 'Enlace reactivado' : 'Enlace actualizado');
      setEditDialog({ open: false, link: null, name: '', expires_at: '' });
      onRefresh();
    } catch (err) {
      notify(err.response?.data?.detail || 'Error al guardar cambios', 'error');
    }
  }, [editDialog, onRefresh]);

  const confirmDelete = useCallback(async (hard = false) => {
    try {
      await axios.delete(`${API}/share/${deleteDialog.link.share_id}${hard ? '?hard=true' : ''}`);
      notify(hard ? 'Enlace eliminado permanentemente' : 'Enlace desactivado');
      setDeleteDialog({ open: false, link: null });
      onRefresh();
    } catch {
      notify('Error al eliminar el enlace', 'error');
    }
  }, [deleteDialog, onRefresh]);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
            Gestión de Enlaces Compartidos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra los accesos generados por los usuarios.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
              <InputLabel>Creado por</InputLabel>
              <Select
                value={filterCreatedBy}
                label="Creado por"
                onChange={(e) => setFilterCreatedBy(e.target.value)}
              >
                <MenuItem value="">Todos los creadores</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.uid} value={u.uid}>{u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={() => onRefresh()}
            disabled={loading}
            size="small"
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : links.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No hay enlaces creados todavía. Usa el botón <b>Compartir</b> en el panel lateral para crear uno.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {links.map(link => {
            const isExpired = link.expires_at && new Date(link.expires_at) <= new Date();
            const isActuallyActive = link.active && !isExpired;
            return (
            <Paper
              key={link.share_id}
              elevation={0}
              sx={{
                p: 2, borderRadius: 2,
                border: '1px solid',
                borderColor: isActuallyActive ? 'rgba(0,87,183,0.15)' : 'rgba(0,0,0,0.1)',
                opacity: isActuallyActive ? 1 : 0.7,
                transition: 'all 0.2s',
                '&:hover': { boxShadow: '0 4px 16px rgba(0,87,183,0.1)' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {/* Status chip */}
                <Chip
                  label={isActuallyActive ? 'ACTIVO' : 'INACTIVO'}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: '0.65rem',
                    bgcolor: isActuallyActive ? '#e8f5e9' : '#fce4ec',
                    color: isActuallyActive ? '#2e7d32' : '#c62828',
                    border: 'none', mt: 0.2
                  }}
                />
                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} noWrap sx={{ mb: 0.2 }}>
                    {link.name || 'Sin nombre'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {formatFilters(link.filters)}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                    {link.created_at ? new Date(link.created_at).toLocaleString('es-DO') : ''}
                    {formatExpiration(link.expires_at)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', display: 'block', mt: 0.2 }}>
                    Creado por: {getUserEmail(link.created_by)}
                  </Typography>
                </Box>
                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {isActuallyActive && (
                    <>
                      <Tooltip title="Abrir enlace">
                        <IconButton size="small" onClick={() => openLink(link.share_id)} color="primary">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copiar enlace">
                        <IconButton size="small" onClick={() => copyLink(link.share_id)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Editar enlace">
                    <IconButton size="small" onClick={() => setEditDialog({ open: true, link, name: link.name || '', expires_at: link.expires_at ? link.expires_at.slice(0, 16) : '' })}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isExpired ? 'Cambia la fecha de vencimiento para reactivar' : (link.active ? 'Desactivar' : 'Activar')}>
                    <span>
                      <Switch
                        size="small"
                        checked={!!isActuallyActive}
                        onChange={() => toggleActive(link)}
                        color="primary"
                        disabled={isExpired}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, link })}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          );
        })}

        {hasMore && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="text" 
              onClick={onLoadMore} 
              disabled={loadingMore}
              sx={{ fontWeight: 700 }}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            >
              {loadingMore ? 'Cargando más...' : 'Cargar más enlaces'}
            </Button>
          </Box>
        )}
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, link: null, name: '', expires_at: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Editar enlace compartido</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Nombre" size="small" sx={{ mt: 1, mb: 3 }}
            value={editDialog.name || ''}
            onChange={e => setEditDialog(d => ({ ...d, name: e.target.value }))}
            placeholder="Ej: Cliente Empresa XYZ"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Fecha de vencimiento (opcional)"
              type="datetime-local"
              fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              value={editDialog.expires_at || ''}
              onChange={e => setEditDialog(d => ({ ...d, expires_at: e.target.value }))}
            />
            {editDialog.expires_at && (
              <Button size="small" color="error" onClick={() => setEditDialog(d => ({ ...d, expires_at: '' }))}>
                Limpiar
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, link: null, name: '' })}>Cancelar</Button>
          <Button onClick={saveEdit} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, link: null })} maxWidth="xs" fullWidth>
        <DialogTitle>¿Qué deseas hacer con este enlace?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            <b>Desactivar</b>: El enlace dejará de funcionar pero podrás reactivarlo después.<br />
            <b>Eliminar permanentemente</b>: Se borrará para siempre.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, link: null })}>Cancelar</Button>
          <Button onClick={() => confirmDelete(false)} color="warning" variant="outlined" startIcon={<DeleteIcon />}>
            Desactivar
          </Button>
          <Button onClick={() => confirmDelete(true)} color="error" variant="contained" startIcon={<DeleteForeverIcon />}>
            Eliminar para siempre
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
